import { STAGES, type Stage, THEMES } from '@fruuit/shared'

/**
 * Display helpers for lane D screens. Dates are shown in UTC, the server's clock
 * (D-14): "7 Oct 2026" and "7 Oct 2026, 10:00 UTC".
 */

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
})

function parse(value: string): Date | null {
  const date = new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function shortDate(value: string | null | undefined, fallback = 'Not set'): string {
  if (!value) return fallback
  const date = parse(value)
  return date ? DATE.format(date) : fallback
}

export function dateTime(value: string | null | undefined, fallback = 'Not set'): string {
  if (!value) return fallback
  const date = parse(value)
  return date ? `${DATE.format(date)}, ${TIME.format(date)} UTC` : fallback
}

/** "No theme recorded" instead of a guessed theme (GAP-UI-005). */
export function themeLabel(theme: string | null | undefined): string {
  if (!theme) return 'No theme recorded'
  return (THEMES.labels as Record<string, string>)[theme] ?? theme
}

export const STAGE_STEPS = ['Baseline', 'Dialogue', 'Commitment', 'Implementation', 'Outcome']

export function stageLevel(stage: string): number {
  return Math.max((STAGES.values as readonly string[]).indexOf(stage), 0)
}

export function stageName(stage: Stage | string): string {
  return stage.replace(/^S[0-4] /, '')
}

export const STAGE_OPTIONS = STAGES.values.map((value) => ({ value, label: stageName(value) }))

export const THEME_OPTIONS = THEMES.values.map((value) => ({ value, label: THEMES.labels[value] }))

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'stalled', label: 'Stalled' },
  { value: 'closed', label: 'Closed' },
]

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}
